import axios from 'axios';
import { auth } from '../services/firebaseConfig';
import { API_BASE, apiFetch } from './backendUrl';
import { API_BASE_SENTINEL } from './backendConfig';
import { getSafeErrorCategory, getSafeFirebaseErrorCode, logAuthDiagnostic } from './authRecovery';
import { isGoogleSlidesExportEnabled } from '../config/featureFlags';

export { API_BASE, apiUrl, apiFetch, sameOriginApiFetch } from './backendUrl';
export const API = API_BASE;
export const SHORT_LINK_FAILURE_TYPES = Object.freeze({
  BACKEND_CONFIG: 'backend_config',
  REQUEST: 'request',
  BACKEND_RESPONSE: 'backend_response',
});
export const SHORT_LINK_ROUTE_SCENARIO_FAST_PARTICIPANT = 'scenario_fast_participant';

const ABSOLUTE_API_PREFIX =
  API_BASE === API_BASE_SENTINEL ? '' : `${API_BASE.replace(/\/+$/, '')}/api/`;
const ABSOLUTE_API_ROOT =
  API_BASE === API_BASE_SENTINEL ? '' : `${API_BASE.replace(/\/+$/, '')}/api`;
let axiosAuthInterceptorRegistered = false;

const CAP_ERROR_MESSAGES = {
  active_map_limit_reached:
    'Your plan has reached its active scenario-map limit. Close a map or review upgrade options in Settings → Plan.',
  free_lifetime_creation_limit_reached:
    'This Free workspace has used both lifetime scenario-map creations. Review Company in Settings → Plan to create another.',
  participant_limit_reached:
    'This scenario has reached its participant limit. Remove a participant or review upgrade options in Settings → Plan.',
  organizer_limit_reached:
    'This workspace has reached its organizer limit. Remove an organizer or review upgrade options in Settings → Plan.',
  free_copied_followup_limit_reached:
    'This Free workspace has used its copied follow-up. Review Company in Settings → Plan for unlimited follow-ups on active maps.',
  free_ai_followup_limit_reached:
    'This Free workspace has used its AI-enhanced follow-up. Review Company in Settings → Plan for unlimited follow-ups on active maps.',
  workspace_over_limit:
    'This workspace is above its current plan allowance. Existing content remains available; reduce usage or review upgrade options in Settings → Plan to continue editing.',
  billing_state_unavailable:
    'Plan status could not be verified. No plan-increasing change was made; please try again.',
  plan_entitlement_unavailable:
    'Plan limits could not be verified. No plan-increasing change was made; please try again.',
  expansion_launch_disabled:
    'Expansion is not available yet.',
  company_checkout_launch_disabled:
    'Company purchasing is not available yet.',
  current_legal_acceptance_required:
    'Review and accept the current Terms and Privacy Notice in Settings → Plan before continuing.',
  limit_active_projects_reached:
    'You’ve reached the maximum number of active projects. Close a project to create another.',
  limit_participants_reached:
    'You’ve reached the maximum participants for this project. Remove someone to add another.',
};

function googleSlidesExportDisabledError() {
  const err = new Error('Google Slides export is disabled.');
  err.code = 'GOOGLE_SLIDES_EXPORT_DISABLED';
  err.status = 404;
  return err;
}

export function getCapErrorMessage(err) {
  const response = err?.response || (err?.status || err?.data ? err : null);
  const code = response?.data?.error;
  return CAP_ERROR_MESSAGES[code] || null;
}

function isBackendApiRequest(url, baseURL) {
  if (!url) return false;
  let candidate = url;
  if (baseURL && !url.startsWith('http')) {
    try {
      candidate = new URL(url, baseURL).toString();
    } catch {
      candidate = url;
    }
  }
  if (candidate.startsWith('/api/')) return true;
  if (candidate.startsWith(ABSOLUTE_API_PREFIX)) return true;
  if (candidate === ABSOLUTE_API_ROOT) return true;
  if (candidate.startsWith(`${ABSOLUTE_API_ROOT}?`)) return true;
  return false;
}

function ensureAxiosAuthInterceptor() {
  if (axiosAuthInterceptorRegistered) return;

  axios.interceptors.request.use(async (config) => {
    if (!isBackendApiRequest(config?.url || '', config?.baseURL)) return config;
    if (API_BASE === API_BASE_SENTINEL) {
      const requestPath = config?.url || config?.path || '<unknown>';
      return Promise.reject(
        new Error(
          `[AffinityAI] Backend URL is not configured. Refusing axios request for "${requestPath}".`,
        ),
      );
    }

    try {
      const token = await auth.currentUser?.getIdToken?.();
      if (token) {
        if (config.headers?.set) {
          config.headers.set('Authorization', `Bearer ${token}`);
        } else {
          config.headers = {
            ...(config.headers || {}),
            Authorization: `Bearer ${token}`,
          };
        }
      }
    } catch (error) {
      logAuthDiagnostic('token.failure', {
        surface: 'axios_interceptor',
        code: getSafeFirebaseErrorCode(error),
        category: getSafeErrorCategory(error),
      });
      // Swallow token errors to avoid breaking unauthenticated flows.
    }
    return config;
  });

  axiosAuthInterceptorRegistered = true; // guard flips once interceptor is registered
}

ensureAxiosAuthInterceptor();

// --- helpers (new) ---
function pickInstanceId(arg) {
  // Supports either { instanceId } or { companyId } in opts.
  if (!arg || typeof arg !== 'object') return null;
  return arg.instanceId || arg.companyId || null;
}

export function withInstanceQuery(path, instanceId) {
  if (!instanceId) return path;
  const sep = path.includes('?') ? '&' : '?';
  return `${path}${sep}instanceId=${encodeURIComponent(instanceId)}`;
}

export function withCompanyIdQuery(path, companyId) {
  if (!companyId) return path;
  const sep = path.includes('?') ? '&' : '?';
  return `${path}${sep}companyId=${encodeURIComponent(companyId)}`;
}

export async function postChatMessage(body, opts = {}) {
  const companyId = opts?.instanceId || opts?.companyId || null;
  const path = withCompanyIdQuery('/api/chat', companyId);
  const res = await apiFetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    credentials: 'include',
    ...opts,
  });
  const flow = res.headers.get('X-Chat-Flow');
  const handler = res.headers.get('X-Chat-Handler');
  if (flow || handler) {
    // eslint-disable-next-line no-console
    console.info('[chat] path', { flow, handler, status: res.status });
  }
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    const err = new Error(data?.error || `HTTP ${res.status}`);
    err.data = data;
    throw err;
  }
  return res.json();
}

/**
 * Update an existing project by ID
 * @param {string} projectId
 * @param {object} payload - Fields to update (background, projectName, keyObjectives, emails, deadline)
 * @return {Promise<object>} Updated project data
 */
export async function updateProject(projectId, payload) {
  const response = await apiFetch(`/api/projects/${projectId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const errorText = await response.text();
    let data = { message: errorText };
    if (errorText) {
      try {
        data = JSON.parse(errorText);
      } catch {
        data = { message: errorText };
      }
    }
    const err = new Error(
      data?.message || data?.error || `Failed to update project: ${response.status} ${errorText}`,
    );
    err.response = { status: response.status, data };
    throw err;
  }
  return response.json();
}

/**
 * Fetch all projects
 * @return {Promise<Array>} List of projects
 */
export async function fetchProjects() {
  const response = await apiFetch('/api/projects');
  if (!response.ok) {
    throw new Error(`Failed to fetch projects: ${response.status}`);
  }
  return response.json();
}

/**
 * Fetch a single project by ID
 * @param {string} projectId
 * @return {Promise<object>} Project data
 */
export async function fetchProject(projectId) {
  const response = await apiFetch(`/api/projects/${projectId}`);
  if (!response.ok) {
    throw new Error(`Failed to fetch project: ${response.status}`);
  }
  return response.json();
}

/**
 * Fetch project lifecycle stats (created, engaged, completed)
 * @param {{period: 'daily'|'weekly'|'monthly'}} options
 */
export async function fetchProjectStats({ period = 'weekly' } = {}) {
  const resp = await apiFetch(`/api/project-stats?period=${period}`);
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(`Failed to fetch project stats: ${resp.status} ${text}`);
  }
  return resp.json();
}

/**
 * Send binary feedback (👍/👎) for a specific AI message.
 * @param {string} companyId
 * @param {string} projectId
 * @param {string} memberId
 * @param {string} messageId
 * @param {'up'|'down'} feedback  // or use '👍' | '👎' if you prefer
 * @return {Promise<object>}   // you can return the written Firestore doc, or just a status
 */
export async function sendChatFeedback(companyId, projectId, memberId, messageId, feedback) {
  const response = await apiFetch('/api/chat-feedback', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ companyId, projectId, memberId, messageId, feedback }),
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to send feedback: ${response.status} ${errorText}`);
  }
  return response.json();
}

function buildContextAssistError(data, status) {
  const err = new Error(data?.message || data?.error || `Context Assist request failed: ${status}`);
  err.status = status;
  err.data = data;
  err.code = data?.error || `context_assist_${status}`;
  return err;
}

async function postContextAssist(path, payload = {}) {
  const response = await apiFetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(payload),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data?.ok === false) {
    throw buildContextAssistError(data, response.status);
  }
  return data;
}

export async function fetchContextAssistChips(payload = {}) {
  return postContextAssist('/api/context-assist/chips', payload);
}

export async function fetchContextAssistBriefing(payload = {}) {
  return postContextAssist('/api/context-assist/briefing', payload);
}

export async function fetchContextAssistConfig() {
  const response = await apiFetch('/api/context-assist/config');
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data?.ok === false) {
    throw buildContextAssistError(data, response.status);
  }
  return data;
}

export async function fetchRuntimeConfig() {
  const response = await apiFetch('/api/config/runtime');
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(`Failed to fetch runtime config: ${response.status}`);
  }
  return data;
}

export async function createTeamMemberShortLink({
  companyId,
  projectId,
  teamMemberId,
  origin,
  route,
  accessMode,
} = {}) {
  const payload = { companyId, projectId, teamMemberId, origin };
  if (route) payload.route = route;
  if (accessMode) payload.accessMode = accessMode;
  let response;
  try {
    response = await apiFetch('/api/short-links', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch (cause) {
    const err = new Error(cause?.message || 'Short link request failed before a response.');
    err.shortLinkFailureType = cause?.shortLinkFailureType || SHORT_LINK_FAILURE_TYPES.REQUEST;
    err.code = cause?.code || 'short_link_request_failed';
    err.isPreNetwork = true;
    err.cause = cause;
    throw err;
  }

  if (!response.ok) {
    const errorText = await response.text();
    let data = { message: errorText };
    try {
      data = errorText ? JSON.parse(errorText) : {};
    } catch {
      data = { message: errorText };
    }
    const err = new Error(data?.error || data?.message || 'Failed to create short link.');
    err.status = response.status;
    err.data = data;
    err.shortLinkFailureType = SHORT_LINK_FAILURE_TYPES.BACKEND_RESPONSE;
    err.code = 'short_link_backend_response';
    throw err;
  }
  return response.json();
}

export async function revokeTeamMemberInvitation({
  companyId,
  projectId,
  teamMemberId,
} = {}) {
  const response = await apiFetch('/api/short-links/revoke', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ companyId, projectId, teamMemberId }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data?.ok === false) {
    const error = new Error(data?.error || 'invitation_revoke_failed');
    error.status = response.status;
    error.data = data;
    throw error;
  }
  return data;
}

export async function resolveShortLinkToken({ token } = {}) {
  const safeToken = String(token || '').trim();
  if (!safeToken) {
    const err = new Error('short_link_token_missing');
    err.status = 400;
    throw err;
  }
  const response = await apiFetch(`/api/t/${encodeURIComponent(safeToken)}/metadata`);
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data?.ok === false) {
    const err = new Error(data?.error || 'short_link_lookup_failed');
    err.status = response.status;
    err.data = data;
    throw err;
  }
  return data;
}

export async function sendTeamMemberProjectEmail({ projectId, teamMemberId, type } = {}) {
  const response = await apiFetch(
    `/api/projects/${encodeURIComponent(projectId)}/team-members/${encodeURIComponent(
      teamMemberId,
    )}/send-email`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type }),
    },
  );

  const errorText = await response.text();
  let data = {};
  try {
    data = errorText ? JSON.parse(errorText) : {};
  } catch {
    data = { message: errorText };
  }

  if (!response.ok) {
    const err = new Error(data?.message || data?.error || 'Failed to send project email.');
    err.status = response.status;
    err.data = data;
    throw err;
  }

  return data;
}

// keep as internal helper
async function resetChat({ instanceId, projectId, teamMemberId, memberId }) {
  const normalizedInstance = typeof instanceId === 'string' ? instanceId.trim() : instanceId;
  const normalizedProject = typeof projectId === 'string' ? projectId.trim() : projectId;
  const resolvedMember = teamMemberId || memberId;
  const normalizedMember =
    typeof resolvedMember === 'string' ? resolvedMember.trim() : resolvedMember;

  const payload = {
    instanceId: normalizedInstance,
    projectId: normalizedProject,
    teamMemberId: normalizedMember,
    memberId: typeof memberId === 'string' ? memberId.trim() : normalizedMember,
  };
  const path = withCompanyIdQuery('/api/reset-chat', normalizedInstance);
  const response = await apiFetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to reset chat: ${response.status} ${errorText}`);
  }
  return response.json();
}

/**
 * Reset only this member’s conversation state
 */
export async function resetMemberConversation(arg1, arg2, arg3) {
  const isObjectPayload = arg1 && typeof arg1 === 'object';
  const payload = isObjectPayload
    ? {
        instanceId: arg1.instanceId,
        projectId: arg1.projectId,
        teamMemberId: arg1.teamMemberId,
      }
    : {
        projectId: arg1,
        teamMemberId: arg2,
        instanceId: arg3,
      };

  if (!payload.instanceId || !payload.projectId || !payload.teamMemberId) {
    throw new Error('Missing instanceId, projectId, or teamMemberId for reset.');
  }

  try {
    return await resetChat(payload);
  } catch (err) {
    // Normalize common cases
    const msg = String(err?.message || '');
    if (msg.includes('404'))
      throw new Error('Reset endpoint not found. Check backend route mapping.');
    if (msg.includes('403')) throw new Error('Not authorized to reset this chat.');
    throw err;
  }
}

export async function getConfiguration(projectType) {
  const resp = await apiFetch(`/api/configuration?projectType=${encodeURIComponent(projectType)}`);
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(`Failed to fetch configuration: ${resp.status} ${text}`);
  }
  return resp.json();
}

export async function updateConfiguration(projectType, data) {
  const resp = await apiFetch(`/api/configuration?projectType=${encodeURIComponent(projectType)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(`Failed to update configuration: ${resp.status} ${text}`);
  }
  return resp.json();
}

// --- deck/export helpers (tenant-aware updates below) ---

export async function forceRebuildDeck(projectId, opts = {}) {
  const instanceId = pickInstanceId(opts);
  const params = new URLSearchParams();
  const force = typeof opts?.force === 'boolean' ? opts.force : true;
  if (force) params.set('force', 'true');
  if (opts?.objectivesOnly === true) params.set('objectivesOnly', 'true');
  const boardDeckMode =
    typeof opts?.boardDeckMode === 'string' ? opts.boardDeckMode.trim().toLowerCase() : '';
  if (boardDeckMode) params.set('boardDeckMode', boardDeckMode);
  const source = typeof opts?.source === 'string' ? opts.source.trim() : '';
  if (source) params.set('source', source);
  const query = params.toString();
  const path = withInstanceQuery(
    `/api/generate-presentation-data/${projectId}${query ? `?${query}` : ''}`,
    instanceId,
  );
  const resp = await apiFetch(path, { method: 'POST' });
  if (!resp.ok) {
    let data = null;
    let text = '';
    try {
      data = await resp.json();
    } catch {
      try {
        text = await resp.text();
      } catch {
        text = '';
      }
    }
    const msg = data?.error || data?.message || text || `Failed to force rebuild: ${resp.status}`;
    const err = new Error(msg);
    err.status = resp.status;
    err.data = data || text;
    throw err;
  }
  return resp.json();
}

async function boardDeckGenerationRequest(projectId, action, opts = {}) {
  const instanceId = pickInstanceId(opts);
  const params = new URLSearchParams();
  const boardDeckMode =
    typeof opts?.boardDeckMode === 'string' ? opts.boardDeckMode.trim().toLowerCase() : '';
  if (boardDeckMode) params.set('boardDeckMode', boardDeckMode);
  const source = typeof opts?.source === 'string' ? opts.source.trim() : '';
  if (source) params.set('source', source);
  if (opts?.force === true) params.set('force', 'true');
  if (opts?.jobId) params.set('jobId', opts.jobId);
  const query = params.toString();
  const path = withInstanceQuery(
    `/api/board-deck-generation/${projectId}/${action}${query ? `?${query}` : ''}`,
    instanceId,
  );
  const init =
    action === 'status'
      ? opts?.signal
        ? { signal: opts.signal }
        : undefined
      : {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          signal: opts?.signal,
          body: JSON.stringify({
            boardDeckMode: boardDeckMode || undefined,
            force: opts?.force === true,
            jobId: opts?.jobId || undefined,
          }),
        };
  const resp = await apiFetch(path, init);
  if (!resp.ok) {
    let data = null;
    let text = '';
    try {
      data = await resp.json();
    } catch {
      try {
        text = await resp.text();
      } catch {
        text = '';
      }
    }
    const msg =
      data?.error || data?.message || text || `Board deck generation failed: ${resp.status}`;
    const err = new Error(msg);
    err.status = resp.status;
    err.data = data || text;
    throw err;
  }
  return resp.json();
}

export function startBoardDeckGeneration(projectId, opts = {}) {
  return boardDeckGenerationRequest(projectId, 'start', opts);
}

export function getBoardDeckGenerationStatus(projectId, opts = {}) {
  return boardDeckGenerationRequest(projectId, 'status', opts);
}

export function workBoardDeckGeneration(projectId, opts = {}) {
  return boardDeckGenerationRequest(projectId, 'work', opts);
}

export function retryBoardDeckGeneration(projectId, opts = {}) {
  return boardDeckGenerationRequest(projectId, 'retry', opts);
}

export async function getGoogleAuthUrl({ companyId, projectId, returnTo } = {}) {
  if (!isGoogleSlidesExportEnabled()) {
    throw googleSlidesExportDisabledError();
  }
  const qs = new URLSearchParams();
  qs.set('mode', 'json');
  if (companyId) qs.set('companyId', companyId);
  if (projectId) qs.set('projectId', projectId);
  if (returnTo) qs.set('returnTo', returnTo);

  const resp = await apiFetch(`/api/auth/google?${qs.toString()}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  });

  const data = await resp.json().catch(() => ({}));
  if (!resp.ok || !data?.url) {
    const msg = data?.error || data?.message || `Failed to start Google OAuth: ${resp.status}`;
    const err = new Error(msg);
    err.data = data;
    err.status = resp.status;
    throw err;
  }
  return data.url;
}

export async function checkGoogleConnected(opts = {}) {
  if (!isGoogleSlidesExportEnabled()) return false;
  const instanceId = pickInstanceId(opts);
  const path = withInstanceQuery('/api/auth/google/status', instanceId);
  try {
    const resp = await apiFetch(path, { method: 'GET' });
    if (resp.ok) {
      const data = await resp.json().catch(() => ({}));
      return Boolean(data?.connected && data?.slidesExportConnected);
    }
    if (resp.status === 401 || resp.status === 403) return false;
    return false;
  } catch {
    return false;
  }
}

export async function exportToGoogleSlides(projectId, payload = {}, query = {}) {
  if (!isGoogleSlidesExportEnabled()) {
    throw googleSlidesExportDisabledError();
  }
  // query may include instanceId already; keep as-is
  const qs = Object.keys(query).length ? `?${new URLSearchParams(query).toString()}` : '';
  const resp = await apiFetch(`/api/export-to-google-slides/${projectId}${qs}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!resp.ok) {
    let data = null;
    let text = '';
    try {
      data = await resp.json();
    } catch {
      try {
        text = await resp.text();
      } catch {
        text = '';
      }
    }
    const msg =
      data?.error || data?.message || text || `Failed to export to Google Slides: ${resp.status}`;
    const err = new Error(`Failed to export to Google Slides: ${resp.status} ${msg}`);
    err.data = data;
    err.status = resp.status;
    throw err;
  }
  const data = await resp.json();
  // normalize URL key
  const url = data.url || data.presentationUrl || data.shareLink || '';
  const fileId = data.fileId || data.id || '';
  return { ...data, url, fileId };
}

export async function fetchExportHistory(projectId, opts = {}) {
  const instanceId = pickInstanceId(opts);
  const path = withInstanceQuery(`/api/exports/${projectId}`, instanceId);
  const resp = await apiFetch(path);
  if (!resp.ok) {
    if (resp.status === 404) return { exports: [] };
    const text = await resp.text();
    throw new Error(`Failed to fetch export history: ${resp.status} ${text}`);
  }
  return resp.json();
}

export async function replayExport(projectId, requestId, body = {}, query = {}) {
  if (!isGoogleSlidesExportEnabled()) {
    throw googleSlidesExportDisabledError();
  }
  // query may include instanceId already; keep as-is
  const qs = Object.keys(query).length ? `?${new URLSearchParams(query).toString()}` : '';
  const resp = await apiFetch(`/api/export-replay/${projectId}/${requestId}${qs}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(`Failed to replay export: ${resp.status} ${text}`);
  }
  return resp.json();
}

export async function importFromSlides(projectId, bodyOrOpts = {}) {
  if (!isGoogleSlidesExportEnabled()) {
    throw googleSlidesExportDisabledError();
  }
  // Back-compat: callers might pass { instanceId } as the only argument.
  const instanceId = pickInstanceId(bodyOrOpts);
  const body = bodyOrOpts && typeof bodyOrOpts === 'object' ? { ...bodyOrOpts } : {};

  // Ensure instanceId is forwarded (backend can read from body)
  if (instanceId && !body.instanceId) body.instanceId = instanceId;

  const path = withCompanyIdQuery(`/api/import-from-slides/${projectId}`, instanceId);
  const resp = await apiFetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(`Failed to import slides: ${resp.status} ${text}`);
  }
  return resp.json();
}

export async function shortenDeckBullets({ projectId, instanceId, ratio, slides }) {
  const payload = { projectId, instanceId, ratio, slides };
  const path = withCompanyIdQuery('/api/deck/shorten', instanceId);
  const resp = await apiFetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(`Failed to shorten bullets: ${resp.status} ${text}`);
  }
  return resp.json();
}

export async function shareBoardOutcome(projectId, payload = {}, opts = {}) {
  const instanceId = pickInstanceId(opts);
  const path = withInstanceQuery(
    `/api/projects/${encodeURIComponent(projectId)}/board-outcomes/share`,
    instanceId,
  );
  const resp = await apiFetch(path, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) {
    const err = new Error(
      data?.message || data?.error || `Failed to share outcome: ${resp.status}`,
    );
    err.status = resp.status;
    err.data = data;
    throw err;
  }
  return data;
}

export async function createFollowUpProject(projectId, opts = {}) {
  const instanceId = pickInstanceId(opts);
  const path = withCompanyIdQuery(
    `/api/projects/${encodeURIComponent(projectId)}/create-follow-up`,
    instanceId,
  );
  const idempotencyKey =
    opts.idempotencyKey ||
    (globalThis.crypto?.randomUUID ?
      globalThis.crypto.randomUUID() :
      `followup-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`);
  const resp = await apiFetch(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Idempotency-Key': idempotencyKey,
    },
    body: JSON.stringify({ companyId: instanceId, idempotencyKey }),
  });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) {
    const err = new Error(
      data?.message || data?.error || `Failed to create follow-up project: ${resp.status}`,
    );
    err.status = resp.status;
    err.data = data;
    throw err;
  }
  return data;
}

export async function copyScenarioFastMapVersion({ companyId, projectId, basedOnVersionId } = {}) {
  const path = `/api/scenario-fast/companies/${encodeURIComponent(
    companyId || '',
  )}/projects/${encodeURIComponent(projectId || '')}/versions/copy`;
  const body = basedOnVersionId ? { basedOnVersionId } : {};
  const resp = await apiFetch(path, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) {
    const err = new Error(
      data?.message || data?.error || `Failed to copy scenario map: ${resp.status}`,
    );
    err.status = resp.status;
    err.data = data;
    throw err;
  }
  return data;
}

export async function fetchScenarioFastOrganizerMap({ companyId, projectId } = {}) {
  const path = `/api/scenario-fast/companies/${encodeURIComponent(
    companyId || '',
  )}/projects/${encodeURIComponent(projectId || '')}/map`;
  const resp = await apiFetch(path, {
    method: 'GET',
    credentials: 'include',
  });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) {
    const err = new Error(
      data?.message || data?.error || `Failed to load scenario map: ${resp.status}`,
    );
    err.status = resp.status;
    err.data = data;
    throw err;
  }
  return data;
}

export async function fetchScenarioFastMapVersions({ companyId, projectId } = {}) {
  const path = `/api/scenario-fast/companies/${encodeURIComponent(
    companyId || '',
  )}/projects/${encodeURIComponent(projectId || '')}/versions`;
  const resp = await apiFetch(path, {
    method: 'GET',
    credentials: 'include',
  });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) {
    const err = new Error(
      data?.message || data?.error || `Failed to load scenario map versions: ${resp.status}`,
    );
    err.status = resp.status;
    err.data = data;
    throw err;
  }
  return data;
}

export async function createScenarioFastAiUpdatePlan({
  companyId,
  projectId,
  basedOnVersionId,
  updateScope,
  updateStyle,
  userChangeBrief,
  thinkingBrief,
} = {}) {
  const path = `/api/scenario-fast/companies/${encodeURIComponent(
    companyId || '',
  )}/projects/${encodeURIComponent(projectId || '')}/versions/ai-update/plan`;
  const resp = await apiFetch(path, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      basedOnVersionId,
      updateScope,
      updateStyle,
      userChangeBrief,
      thinkingBrief,
    }),
  });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) {
    const err = new Error(
      data?.message || data?.error || `Failed to create scenario map update plan: ${resp.status}`,
    );
    err.status = resp.status;
    err.data = data;
    throw err;
  }
  return data;
}

export async function createScenarioFastAiUpdateQuestions({
  companyId,
  projectId,
  planId,
  basedOnVersionId,
} = {}) {
  const path = `/api/scenario-fast/companies/${encodeURIComponent(
    companyId || '',
  )}/projects/${encodeURIComponent(projectId || '')}/versions/ai-update/questions`;
  const resp = await apiFetch(path, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      planId,
      basedOnVersionId,
    }),
  });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) {
    const err = new Error(
      data?.message ||
        data?.error ||
        `Failed to create scenario map update questions: ${resp.status}`,
    );
    err.status = resp.status;
    err.data = data;
    throw err;
  }
  return data;
}

export async function generateScenarioFastAiUpdatedMapVersion({
  companyId,
  projectId,
  planId,
  questionsId,
  basedOnVersionId,
  answers,
} = {}) {
  const path = `/api/scenario-fast/companies/${encodeURIComponent(
    companyId || '',
  )}/projects/${encodeURIComponent(projectId || '')}/versions/ai-update/generate`;
  const resp = await apiFetch(path, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      planId,
      questionsId,
      basedOnVersionId,
      answers,
    }),
  });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) {
    const err = new Error(
      data?.message || data?.error || `Failed to generate updated scenario map: ${resp.status}`,
    );
    err.status = resp.status;
    err.data = data;
    throw err;
  }
  return data;
}

export async function fetchSharedBoardOutcome(token) {
  const safeToken = encodeURIComponent(String(token || '').trim());
  const resp = await apiFetch(`/api/board-outcomes/shared/${safeToken}`);
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) {
    const err = new Error(
      data?.message || data?.error || `Failed to load shared outcome: ${resp.status}`,
    );
    err.status = resp.status;
    err.data = data;
    throw err;
  }
  return data;
}

export async function startExportJob(projectId, payload = {}, query = {}) {
  if (!isGoogleSlidesExportEnabled()) {
    throw googleSlidesExportDisabledError();
  }
  // query may include instanceId already; keep as-is
  const qs = Object.keys(query).length ? `?${new URLSearchParams(query).toString()}` : '';
  const resp = await apiFetch(`/api/export-jobs/${projectId}${qs}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(`Failed to start export job: ${resp.status} ${text}`);
  }
  return resp.json();
}

export async function getExportJob(projectId, jobId, opts = {}) {
  const instanceId = pickInstanceId(opts);
  const path = withInstanceQuery(`/api/export-jobs/${projectId}/${jobId}`, instanceId);
  const resp = await apiFetch(path);
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(`Failed to fetch export job: ${resp.status} ${text}`);
  }
  return resp.json();
}

/**
 * Retry generation for a single slide
 * @param {string} projectId
 * @param {number} slideId
 */
export async function retrySlide(projectId, slideId, opts = {}) {
  const instanceId = pickInstanceId(opts);
  const path = withInstanceQuery(`/api/retry-slide/${projectId}/${slideId}`, instanceId);
  const resp = await apiFetch(path, { method: 'POST' });
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(`Failed to retry slide: ${resp.status} ${text}`);
  }
  return resp.json();
}
